import { format, parseISO, isValid } from 'date-fns';

export type Timezone = 'PST' | 'GMT' | 'IST';

export const timezoneOptions = [
  { value: 'PST' as Timezone, label: 'PST/PDT (Pacific)', offset: -8, dstOffset: -7 },
  { value: 'GMT' as Timezone, label: 'GMT (Greenwich)', offset: 0, dstOffset: 0 },
  { value: 'IST' as Timezone, label: 'IST (India)', offset: 5.5, dstOffset: 5.5 },
];

// Function to determine if a date falls within daylight saving time for PST
function isDaylightSavingTime(date: Date): boolean {
  const year = date.getFullYear();
  
  // DST in the US starts on the second Sunday in March
  const marchSecondSunday = new Date(year, 2, 8); // March 8th
  marchSecondSunday.setDate(marchSecondSunday.getDate() + (7 - marchSecondSunday.getDay()) % 7);
  
  // DST ends on the first Sunday in November
  const novemberFirstSunday = new Date(year, 10, 1); // November 1st
  novemberFirstSunday.setDate(novemberFirstSunday.getDate() + (7 - novemberFirstSunday.getDay()) % 7);
  
  return date >= marchSecondSunday && date < novemberFirstSunday;
}

export function convertTimezone(
  dateInput: string | Date,
  fromTimezone: Timezone = 'PST',
  toTimezone: Timezone = 'PST'
): Date | null {
  try {
    let date: Date;
    
    // Parse the input date
    if (typeof dateInput === 'string') {
      if (dateInput.includes('/') && (dateInput.includes('AM') || dateInput.includes('PM'))) {
        date = new Date(dateInput);
      } else {
        date = parseISO(dateInput);
      }
    } else {
      date = dateInput;
    }

    if (!isValid(date)) {
      return null;
    }

    // If same timezone, return as is
    if (fromTimezone === toTimezone) {
      return date;
    }

    // Get timezone options
    const fromTzOption = timezoneOptions.find(tz => tz.value === fromTimezone);
    const toTzOption = timezoneOptions.find(tz => tz.value === toTimezone);
    
    if (!fromTzOption || !toTzOption) {
      return null;
    }

    // Determine the correct offset based on daylight saving time
    let fromOffset = fromTzOption.offset;
    let toOffset = toTzOption.offset;
    
    // Apply DST correction for PST/PDT
    if (fromTimezone === 'PST' && isDaylightSavingTime(date)) {
      fromOffset = fromTzOption.dstOffset;
    }
    if (toTimezone === 'PST' && isDaylightSavingTime(date)) {
      toOffset = toTzOption.dstOffset;
    }

    // Calculate the difference between timezones
    const offsetDifference = toOffset - fromOffset;
    
    // Apply the timezone offset difference
    const convertedTime = date.getTime() + (offsetDifference * 60 * 60 * 1000);
    
    return new Date(convertedTime);
  } catch (error) {
    console.error('Error converting timezone:', error);
    return null;
  }
}

export function formatDateInTimezone(
  dateInput: string | Date,
  selectedTimezone: Timezone = 'PST',
  formatString: string = 'MMM d, yyyy HH:mm'
): string {
  const convertedDate = convertTimezone(dateInput, 'PST', selectedTimezone);
  
  if (!convertedDate) {
    return 'Invalid Date';
  }

  return format(convertedDate, formatString);
}

export function getTimezoneAbbreviation(timezone: Timezone): string {
  switch (timezone) {
    case 'PST':
      return 'PST/PDT';
    case 'GMT':
      return 'GMT';
    case 'IST':
      return 'IST';
    default:
      return 'PST/PDT';
  }
} 